import { useMemo } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { resources } from "@constants/hardcoded-strings";
import { useModalContext } from "@context/use-modal-context";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ModalTypes } from "@enums/modal-types";
import { FormattedRpServiceConfiguration, FormattedRpServiceConfigurationIdentityProvider } from "@idw-types/data/rp-service-configuration";
import { ContentPageBody } from "../../../../common/content-page-body/content-page-body";
import { ContentPageHeader } from "../../../../common/content-page-header/content-page-header";
import { LabelValueContainer } from "../../../../common/label-value-container/label-value-container";
import { AddRpServiceConfigurationIdentityProviderModal } from "../../../../modals/add-rp-service-configuration-identity-provider-modal/add-rp-service-configuration-identity-provider-modal";
import commonStyles from "../../verification-service-details-component.less";
import styles from "./federated-idp-identity-providers-tab-container.less";

type FederatedIdpIdentityProvidersTabContainerProps = {
    isDisabled: boolean;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    setRpServiceConfigurationState: (state: FormattedRpServiceConfiguration) => void;
};

export const FederatedIdpIdentityProvidersTabContainer = (props: FederatedIdpIdentityProvidersTabContainerProps) => {
    const { isDisabled, rpServiceConfigurationState, setRpServiceConfigurationState } = props;

    const modalContext = useModalContext();

    const selectedIdentityProviders = useMemo(() => {
        return rpServiceConfigurationState.idps.filter(idp => idp.addedToService);
    }, [rpServiceConfigurationState.idps]);

    const unselectedIdentityProviders = useMemo(() => {
        return rpServiceConfigurationState.idps.filter(idp => !idp.addedToService);
    }, [rpServiceConfigurationState.idps]);

    const addRpServiceConfigurationIdentityProvider = (identityProvider: FormattedRpServiceConfigurationIdentityProvider) => {
        const newIdentityProviders = rpServiceConfigurationState.idps.map(idp => idp.identityProvider.id === identityProvider.identityProvider.id
            ? identityProvider
            : idp
        );
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            idps: newIdentityProviders
        });
    };

    const onClickRemoveIdentityProvider = (identityProvider: FormattedRpServiceConfigurationIdentityProvider) => {
        const newIdentityProviders = rpServiceConfigurationState.idps.map(idp => idp.identityProvider.id === identityProvider.identityProvider.id
            ? { ...identityProvider, addedToService: false }
            : idp
        );
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            idps: newIdentityProviders
        });
    };

    const onClickAddIdentityProvider = () => {
        const renderAddIdentityProviderModal = (onModalClose: () => void) => {
            return (
                <AddRpServiceConfigurationIdentityProviderModal
                    rpServiceConfigurationIdentityProviders={unselectedIdentityProviders}
                    addRpServiceConfigurationIdentityProvider={addRpServiceConfigurationIdentityProvider}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            renderFunction: renderAddIdentityProviderModal,
            modalType: ModalTypes.AddIdentityProvider
        });
    };

    const identityProvidersHeaderButton = (
        (!isDisabled) ? (
            <Button disabled={unselectedIdentityProviders.length === 0} onClick={onClickAddIdentityProvider}>
                {resources.pages.verificationServiceDetails.tabs.federatedIdp.subTabs.identityProviders.buttons.addIdentityProvider.label}
            </Button>
        ) : null
    );

    return (
        <div>
            <ContentPageHeader
                title={resources.pages.verificationServiceDetails.tabs.federatedIdp.subTabs.identityProviders.title}
                headerButton={identityProvidersHeaderButton}
            />
            <ContentPageBody isMaxWidth={true}>
                <span className={commonStyles.description}>
                    {resources.pages.verificationServiceDetails.tabs.federatedIdp.subTabs.identityProviders.description}
                </span>
                {selectedIdentityProviders.length === 0 && (
                    <Alert variant={"danger"}>
                        {resources.pages.verificationServiceDetails.tabs.federatedIdp.subTabs.identityProviders.noIdentityProvidersMessage(isDisabled)}
                    </Alert>
                )}
                {selectedIdentityProviders.length > 0 && (
                    <div>
                        {selectedIdentityProviders.map(identityProvider => (
                            <LabelValueContainer
                                controlId={`identity-provider-${identityProvider.identityProvider.id}-label-value`}
                                key={`identity-provider-${identityProvider.identityProvider.id}`}
                                className={styles.identityProviderContainer}
                                label={identityProvider.identityProvider.name}
                                labelWidth={LabelWidthTypes.EXTRA_SMALL}
                                layout={LayoutTypes.ROW}
                                labelIsBold={true}
                                value={" "}
                                afterValueIcon={isDisabled ? null : faTrash}
                                afterValueIconDisabled={isDisabled}
                                afterValueIconOnClick={() => onClickRemoveIdentityProvider(identityProvider)}
                                afterValueIconContainerMarginLeftAuto={true}
                            />
                        ))}
                    </div>
                )}
            </ContentPageBody>
        </div>
    );
};